import React, {useEffect, useState} from "react";
import * as Yup from "yup";
import { useFormikContext, Formik } from "formik";
import { Button, Form } from "react-bootstrap";
import { Flag } from "react-feather";
import Spinner from 'react-bootstrap/Spinner';
import Select from "react-select";

const { REACT_APP_BACKEND_URL } = process.env;

const ProviderSelect = ({ token, questionId, index, handleProviderChanged, selectedProviders}) => {
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [data, setData] = useState([]);
  
  const { setFieldValue, setFieldTouched } = useFormikContext();

  useEffect(() => {
    if (token != null && token !== "")
    {
      fetch(REACT_APP_BACKEND_URL + "disclosures/admin/" + token + "/providers")
      .then(res => {
          if (res.ok)
          {
              return res.json();
          }
    
          throw Error(res.status);
      })
      .then(
        (result) => {
          setIsLoaded(true);
          setData(result);
        },
        (error) => {
          setIsLoaded(true);
          setError(error);
        }
      )
    }
    else
    {
      setIsLoaded(false);
      setData([]);
    }
  }, [token])
  
  const options = data.map((provider) => {
      return {value: provider.id, label: provider.name};
  });

  if (!isLoaded)
  {
    return (<Form.Select disabled={true}><option value={0} key={0}>Loading...</option></Form.Select>);
  }
  else if (error || !data)
  {
    return (<Form.Select disabled={true}><option value={0} key={0}>Error... Please reload the page...</option></Form.Select>);
  }
  else
  {
      return (
          <div style={{width: "350px"}}>
              <Select
              options={options}
              onChange={(selectedProviders) => {
                  handleProviderChanged(questionId, index, setFieldValue, setFieldTouched, selectedProviders || []);
              }}
              value={selectedProviders || []}
              placeholder="Select provider(s)..."
              isMulti
              theme={(theme) => ({
                ...theme,
                colors: {
                ...theme.colors,
                  primary50: '#DEF2F1',
                  primary25: '#DEF2F1',
                  primary: '#3AAFA9',
                },
              })}
              />
          </div>
      )
  }
}



const AdminDisclosures = ({token, data}) => {
  const [providerIds, setProviderIds] = useState({});
  const [selectedProviders, setSelectedProviders] = useState({});

  const handleProviderChanged = (questionId, index, setFieldValue, setFieldTouched, providers) => {
    setSelectedProviders(prev => ({
      ...prev,
      [questionId]: providers,          // new key, new parent object
    }));

    setProviderIds(prev => ({
      ...prev,
      [questionId]: providers.map(p => p.value),
    }));

    setFieldValue(`disclosures[${index}].providerIds`, providers.map(p => p.value));
    setFieldTouched(`disclosures[${index}].providerIds`, true, false);
  }

  const initialValues = {disclosures:[]}

  if (data && data.disclosures)
  {
    for (let i = 0; i < data.disclosures.length; i++)
    {
      data.disclosures[i].answer = null;
      data.disclosures[i].explanation = "";

      data.disclosures[i].providerIds = [];

      initialValues.disclosures.push(data.disclosures[i]);
    }
  }

  return (
    <Formik
      enableReinitialize={true}
      initialValues={initialValues}
      validationSchema={Yup.object().shape({
        disclosures: Yup.array().of(
          Yup.object().shape({
            answer: Yup.string().oneOf(["YES", "NO"]).nullable().required("Please select an answer"),
            explanation: Yup.string().max(1027)
              .when("answer", {
                is: "YES",
                then: Yup.string().required("Explanation is required"),
                otherwise: Yup.string()
            }),
            providerIds: Yup.array().of(Yup.number())
              .when("answer", {
                is: "YES",
                then: s => s.min(1, "Select at least one provider"),
                otherwise: s => s, // no requirement when NO
            }),
          })
        )
      })}
      onSubmit={async (values, { setErrors, setStatus, resetForm, setSubmitting }) => {
        try {
          let formData = new FormData();

          formData.append("data", new Blob([JSON.stringify(
            {
              disclosures: values.disclosures
            })], {
              type: "application/json"
          }));

          const requestMetadata = {
            method: 'POST',
            body: formData
          };

          const res = await fetch(REACT_APP_BACKEND_URL + "disclosures/admin/" + token, requestMetadata);

          if (res.status >= 200 && res.status <= 299)
          {
            window.location.reload();
          }
          else if (res.status === 400)
          {
            setStatus({ success: false });
            alert("Unable to submit; please disable any ad blockers and/or antivirus software and try again. If the problem persists, using a different browser may help.");
            setSubmitting(false);
          }
          else
          {
            throw Error(res.statusText);
          }
        } catch (error) {
          const message = "Something went wrong; please try again";

          setStatus({ success: false });
          alert(message);
          setSubmitting(false);
        }
      }}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        setFieldValue,
        status,
        touched,
        values,
        getFieldProps,
      }) => (
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <ol>
            <Form.Label style={{fontWeight: "bold"}}>{data.surveyName}
            <br /><span style={{fontSize: '0.9em', fontWeight: "normal"}}><Flag style={{height: "16px", width: "16px", color: "#2B7A78"}} /> Please answer the following Yes / No questions. If yes, please specify which provider(s).</span>  
            </Form.Label><br />
            {values.disclosures.map((disclosure, index) => {
              let showHeader = index === 0 || values.disclosures[index - 1].header !== disclosure.header;
              return (
                <React.Fragment key={disclosure.questionId}>
                {showHeader && <><br /><Form.Label style={{fontWeight: "bold"}}>{disclosure.header}</Form.Label></>}
                <li>{disclosure.question}
                <br />
                <Form.Check style={{display: "inline"}}
                  type="radio"
                  onChange={(e) => {
                    setFieldValue(`disclosures[${index}].answer`, "YES");
                  }}
                  onBlur={handleBlur}
                  checked={disclosure.answer === 'YES'}
                  name={`disclosures[${index}].answer`}
                  isInvalid={!!touched.disclosures?.[index]?.answer && !!errors.disclosures?.[index]?.answer}
                />&nbsp;Yes&nbsp;&nbsp;
                <Form.Check style={{display: "inline"}}
                  type="radio"
                  onChange={(e) => {
                    setFieldValue(`disclosures[${index}].providerIds`, [], false);  // don't trigger two validations
                    setFieldValue(`disclosures[${index}].explanation`, "", false);
                    setFieldValue(`disclosures[${index}].answer`, "NO");
                    
                    setSelectedProviders(prev => ({
                      ...prev,
                      [disclosure.questionId]: []
                    }));
                    setProviderIds(prev => ({
                      ...prev,
                      [disclosure.questionId]: []
                    }));
                  }}
                  onBlur={handleBlur}
                  checked={disclosure.answer === 'NO'}
                  name={`disclosures[${index}].answer`}
                  isInvalid={!!touched.disclosures?.[index]?.answer && !!errors.disclosures?.[index]?.answer}
                />&nbsp;No
                </li>
                {!!touched.disclosures?.[index]?.answer && errors.disclosures?.[index]?.answer && (
                  <Form.Control.Feedback style={{ display: "block" }} type="invalid">
                    {errors.disclosures?.[index]?.answer}
                  </Form.Control.Feedback>
                )}
                {disclosure.answer === 'YES' &&
                  <>
                  <ProviderSelect token={token} questionId={disclosure.questionId} index={index} handleProviderChanged={handleProviderChanged} selectedProviders={selectedProviders[disclosure.questionId]}/>
                  {!!touched.disclosures?.[index]?.providerIds && errors.disclosures?.[index]?.providerIds && (
                    <Form.Control.Feedback type="invalid" style={{ display: "block" }}>
                      {errors.disclosures?.[index]?.providerIds}
                    </Form.Control.Feedback>
                  )}
                  <Form.Control
                    as="textarea"
                    style={{marginTop: "5px"}}
                    placeholder="Please provide additional details"
                    {...getFieldProps(`disclosures[${index}].explanation`)}
                    isInvalid={Boolean(touched.disclosures?.[index]?.explanation && errors.disclosures?.[index]?.explanation)}
                  />
                  {!!touched.disclosures?.[index]?.explanation && errors.disclosures?.[index]?.explanation && (
                    <Form.Control.Feedback type="invalid">
                      {errors.disclosures?.[index]?.explanation}
                    </Form.Control.Feedback>
                  )}
                  </>
                }
                <br />
                </React.Fragment>
            );
            })}
            </ol>
          </Form.Group>
          <br />
          {isSubmitting ?
          <div className="text-center">
             <br />
            <Spinner size="sm" animation="border" />
          </div>
          :
          <>
            <div id="submitButton" className="text-center mt-3 float-end">
              <Button
                type="submit"
                variant="primary"
                size="lg"
                disabled={isSubmitting}
              >
                Submit
              </Button>
            </div>
          </>
          }
          <br /><br /><br />
          {
            Object.keys(errors).length > 0 && Object.keys(touched).length > 0 &&
            <div className="text-center mt-3"><span className="invalid-feedback" style={{display: "inline-block"}}>Please correct the errors above before completing the submission</span></div>
          }
        </Form>
      )}
    </Formik>
  );
}

export default AdminDisclosures;
